import { sessionStateSchema, type SessionState } from '@hearth/shared';
import { queryOptions, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import { apiRequest, setCsrfToken } from './api';

export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: async ({ signal }): Promise<SessionState> => {
    const state = await apiRequest('/api/auth/session', { schema: sessionStateSchema, signal });
    setCsrfToken(state.authenticated ? state.csrfToken : undefined);
    return state;
  },
  staleTime: 30_000,
});

export type AuthenticatedSession = Extract<SessionState, { authenticated: true }>;

/** Session inside the signed-in layout, where the route guard guarantees authentication. */
export function useAuthenticatedSession(): AuthenticatedSession {
  const { data } = useSuspenseQuery(sessionQuery);
  if (!data.authenticated)
    throw new Error('useAuthenticatedSession used outside the signed-in layout');
  return data;
}

/**
 * After the signed-in identity changes (impersonation start/stop): drop every cached query
 * that belonged to the previous identity, and refetch the session so mounted components
 * (header, banners) update in place. Use queryClient.clear() only when leaving the app.
 */
export async function switchIdentity(queryClient: QueryClient): Promise<void> {
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== sessionQuery.queryKey[0] });
  await queryClient.refetchQueries({ queryKey: sessionQuery.queryKey });
}
